/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ID } from '@gh/common-shared-data';
import {
  MutableDataSource,
  SlicedData,
  SlicedDataQuery,
  SlicedDataSource,
  SortDirection,
} from '@gh/core-data';
import { arrayIterator, CollectionComparator } from '@gh/core-util';
import { of$ } from '@gh/rx';
import { distinctUntilChanged$, map$ } from '@gh/rx/operators';
import { constant, reverse, sortBy } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { createTableDriver } from './table/table';
import { createOrderIndexDriver } from './table/table-order-index';
import { Table, TableDriver } from './table/table.interfaces';

export class DataSet<T> implements SlicedDataSource<T>, MutableDataSource<T> {
  private driver: TableDriver<T>;
  private data$$: BehaviorSubject<Table<T>>;

  constructor(private getId: TransformFn<T, any>, comparator: CollectionComparator<T>, data: T[] = []) {
    const driver = createTableDriver(getId, createOrderIndexDriver('order', getId, constant(true), comparator));

    this.driver = driver;
    this.data$$ = new BehaviorSubject(driver.create(arrayIterator(data)));
  }

  get data(): T[] {
    return this.driver.byOrder(this.table, 'order');
  }

  get dataAsObservable(): Observable<T[]> {
    return this.data$$
      .asObservable().pipe(
        map$((table) => this.driver.byOrder(table, 'order')),
        distinctUntilChanged$());
  }

  private get table(): Table<T> {
    return this.data$$.value;
  }

  byId(id: ID): Maybe<T> {
    return this.driver.byId(this.table, id);
  }

  byIdAsObservable(id: ID): Observable<Maybe<T>> {
    return this.data$$
      .asObservable().pipe(
        map$((table) => this.driver.byId(table, id)),
        distinctUntilChanged$());
  }

  select(query: SlicedDataQuery): Observable<SlicedData<T>> {
    const { from, to, sorting } = query;
    const { data } = this;

    const sliced = data.slice(from, to);

    if (sorting) {
      const sorted = sortBy(sliced, sorting.field);
      return of$({
        from,
        to,
        total: data.length,
        data: sorting.direction === SortDirection.Descending ? reverse(sorted) : sorted,
      });
    } else {
      return of$({ from, to, total: data.length, data });
    }
  }

  create(entity: T): void {
    this.data$$.next(this.driver.insert(this.data$$.value, entity));
  }

  update(entity: T): void {
    this.data$$.next(this.driver.update(this.data$$.value, entity));
  }

  remove(entity: T): void {
    this.data$$.next(this.driver.remove(this.data$$.value, this.getId(entity)));
  }

  set(data: T[]): void {
    this.data$$.next(this.driver.create(arrayIterator(data)));
  }

  clear(): void {
    this.set([]);
  }
}
